import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { IconHelp, IconPlus, IconSave, IconTrash } from '../../icons';
import { DialogWindow } from '../DialogWindow';
import IconButton from '../core/IconButton';
import { useTagMutations, useTags } from '../../hooks/useTags';
import useGuideStore from '../../store/guide';
import useTagManagementDialog from '../../store/tagManagementDialog';
import TagSelector from './TagSelector';
import Input from '@/components/core/Input';
import Button from '@/components/core/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import UnsavedChangesDialog from '../UnsavedChangesDialog';

type PendingNavigation =
  | { type: 'close' }
  | { type: 'select'; tagId?: string }
  | { type: 'create' };

const messages = defineMessages({
  name: {
    id: 'name',
    defaultMessage: 'Name',
  },
  description: {
    id: 'description',
    defaultMessage: 'Description',
  },
  save: {
    id: 'save',
    defaultMessage: 'Save',
  },
  remove: {
    id: 'remove',
    defaultMessage: 'Remove',
  },
  removeTag: {
    id: 'tag_management_dialog.remove_tag',
    defaultMessage:
      'Remove tag. Will automatically unassign the tag from all workorders. This cannot be undone.',
  },
  newTag: {
    id: 'tag_management_dialog.new_tag',
    defaultMessage: 'New tag',
  },
  editTag: {
    id: 'tag_management_dialog.edit_tag',
    defaultMessage: 'Edit tag',
  },
  manageTags: {
    id: 'tag_management_dialog.manage_tags',
    defaultMessage: 'Manage tags',
  },
  help: {
    id: 'help',
    defaultMessage: 'Help',
  },
  tagCreated: {
    id: 'tag_management_dialog.tag_created',
    defaultMessage: 'Tag created',
  },
  tagUpdated: {
    id: 'tag_management_dialog.tag_updated',
    defaultMessage: 'Tag updated',
  },
  tagRemoved: {
    id: 'tag_management_dialog.tag_removed',
    defaultMessage: 'Tag removed',
  },
});

const TagManagementDialog: FC = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { openGuide } = useGuideStore();
  const { show, close } = useTagManagementDialog();
  const { lookup: tagsLookup } = useTags();
  const {
    create: { mutateAsync: createTag, isLoading: createProcessing },
    update: { mutateAsync: updateTag, isLoading: updateProcessing },
    remove: { mutateAsync: removeTag, isLoading: removeProcessing },
  } = useTagMutations();

  const [selectedTagId, setSelectedTagId] = useState<string | undefined>(
    undefined,
  );
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isCreatingNewTag, setIsCreatingNewTag] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);

  const isDirty = useMemo(() => {
    if (isCreatingNewTag) {
      return name !== '' || description !== '';
    }
    if (!selectedTagId) {
      return false;
    }
    const selectedTag = tagsLookup[selectedTagId];
    return (
      name !== (selectedTag?.name ?? '') ||
      description !== (selectedTag?.description ?? '')
    );
  }, [description, isCreatingNewTag, name, selectedTagId, tagsLookup]);
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;

  const resetDefaults = (): void => {
    setName('');
    setDescription('');
    setSelectedTagId(undefined);
    setIsCreatingNewTag(false);
    setConfirmDeleteOpen(false);
    setPendingNavigation(null);
  };

  const onClose = useCallback(() => {
    resetDefaults();
    close(true);
  }, [close]);

  const applyNavigation = useCallback(
    (nav: PendingNavigation) => {
      setPendingNavigation(null);
      if (nav.type === 'close') {
        onClose();
        return;
      }
      if (nav.type === 'create') {
        setIsCreatingNewTag(true);
        setName('');
        setDescription('');
        setSelectedTagId(undefined);
        return;
      }
      setSelectedTagId(nav.tagId);
      setName(nav.tagId ? tagsLookup[nav.tagId]?.name ?? '' : '');
      setDescription(nav.tagId ? tagsLookup[nav.tagId]?.description ?? '' : '');
      setIsCreatingNewTag(false);
    },
    [onClose, tagsLookup],
  );

  const requestNavigation = useCallback(
    (nav: PendingNavigation) => {
      if (
        nav.type === 'select' &&
        nav.tagId === selectedTagId &&
        !isCreatingNewTag
      ) {
        return;
      }
      if (nav.type === 'create' && isCreatingNewTag) {
        return;
      }
      if (isDirtyRef.current) {
        setPendingNavigation(nav);
        return;
      }
      applyNavigation(nav);
    },
    [applyNavigation, isCreatingNewTag, selectedTagId],
  );

  const tryClose = useCallback(() => {
    if (pendingNavigation || confirmDeleteOpen) {
      return;
    }
    requestNavigation({ type: 'close' });
  }, [confirmDeleteOpen, pendingNavigation, requestNavigation]);

  const processing = useMemo(() => {
    return createProcessing || updateProcessing || removeProcessing;
  }, [createProcessing, removeProcessing, updateProcessing]);

  const onCreateTag = useCallback(() => {
    const newName = name.trim();
    if (newName.length === 0) {
      return;
    }
    const newDescription = description.trim();
    void createTag({
      name: newName,
      description: newDescription,
    }).then((tag) => {
      setSelectedTagId(tag.id);
      setIsCreatingNewTag(false);
      enqueueSnackbar(<FormattedMessage {...messages.tagCreated} />, {
        variant: 'success',
      });
    });
  }, [createTag, description, enqueueSnackbar, name]);

  const onUpdateTag = useCallback(() => {
    if (!selectedTagId) {
      return;
    }
    const newName = name.trim();
    const newDescription = description.trim();
    void updateTag({
      id: selectedTagId,
      tag: {
        name: newName ?? tagsLookup[selectedTagId]?.name ?? '',
        description:
          newDescription ?? tagsLookup[selectedTagId]?.description ?? '',
        flags: tagsLookup[selectedTagId]?.flags ?? undefined,
      },
    }).then(() => {
      enqueueSnackbar(<FormattedMessage {...messages.tagUpdated} />, {
        variant: 'success',
      });
    });
  }, [
    description,
    enqueueSnackbar,
    name,
    selectedTagId,
    tagsLookup,
    updateTag,
  ]);

  const onRemoveTag = useCallback(() => {
    if (!selectedTagId) {
      return;
    }
    void removeTag(selectedTagId).then(() => {
      resetDefaults();
      enqueueSnackbar(<FormattedMessage {...messages.tagRemoved} />, {
        variant: 'success',
      });
    });
  }, [enqueueSnackbar, removeTag, selectedTagId]);

  const onSelectTag = useCallback(
    (tagIds: string[]) => {
      const tagId = [...tagIds].filter((id) => id !== selectedTagId)[0];
      requestNavigation({ type: 'select', tagId });
    },
    [requestNavigation, selectedTagId],
  );

  return (
    <>
      <ConfirmDialog
        open={confirmDeleteOpen}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={onRemoveTag}
      >
        <FormattedMessage {...messages.removeTag} />
      </ConfirmDialog>
      <UnsavedChangesDialog
        open={!!pendingNavigation}
        onCancel={() => setPendingNavigation(null)}
        onOk={() => {
          if (pendingNavigation) {
            applyNavigation(pendingNavigation);
          }
        }}
      />
      <DialogWindow
        open={show}
        title={intl.formatMessage(messages.manageTags)}
        onOpenChange={(isOpen) => !isOpen && tryClose()}
        headerActions={
          <IconButton
            variant="ghost"
            icon={<IconHelp />}
            aria-label={intl.formatMessage(messages.help)}
            onClick={() => openGuide('TagManagementDialog')}
          />
        }
        content={
          <div className="flex h-fit gap-4">
            <div className="flex w-1/2 flex-col">
              <TagSelector
                selectedTagIds={selectedTagId ? [selectedTagId] : []}
                excludeUneditable
                onChange={onSelectTag}
              />
              <Button
                startIcon={<IconPlus />}
                variant="filled"
                onClick={() => requestNavigation({ type: 'create' })}
                className="mt-auto"
                disabled={isCreatingNewTag}
              >
                {intl.formatMessage(messages.newTag)}
              </Button>
            </div>
            <div className="flex min-h-[300px] grow flex-col justify-start rounded bg-workorder-100 p-4">
              <div className="flex flex-col gap-10">
                <h3 className="text-lg font-bold">
                  {isCreatingNewTag
                    ? intl.formatMessage(messages.newTag)
                    : intl.formatMessage(messages.editTag)}
                </h3>
                <Input
                  id="tag-name"
                  label={intl.formatMessage(messages.name)}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!selectedTagId && !isCreatingNewTag}
                />
                <Input
                  id="tag-description"
                  label={intl.formatMessage(messages.description)}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!selectedTagId && !isCreatingNewTag}
                />
              </div>
              <div className="mt-auto flex justify-between gap-2">
                {!isCreatingNewTag ? (
                  <Button
                    startIcon={<IconTrash />}
                    variant="ghost"
                    color="red"
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={!selectedTagId && !isCreatingNewTag}
                  >
                    {intl.formatMessage(messages.remove)}
                  </Button>
                ) : (
                  <Button variant="ghost" color="red" onClick={resetDefaults}>
                    <FormattedMessage id="cancel" defaultMessage="Cancel" />
                  </Button>
                )}
                <Button
                  startIcon={<IconSave />}
                  variant="filled"
                  onClick={isCreatingNewTag ? onCreateTag : onUpdateTag}
                  loading={processing}
                  disabled={
                    isCreatingNewTag
                      ? !name.trim().length
                      : !name.trim().length ||
                        (name === tagsLookup[selectedTagId ?? '']?.name &&
                          description ===
                            tagsLookup[selectedTagId ?? '']?.description)
                  }
                >
                  {intl.formatMessage(messages.save)}
                </Button>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
};

export default TagManagementDialog;
